import { useEffect, useState } from "react";
import {
  AutoComplete,
  Card,
  Radio,
  Segmented,
  Select,
  Space,
  Tabs,
  Tooltip,
  Typography,
} from "antd";
import { ThunderboltOutlined } from "@ant-design/icons";
import TopTalkersTable from "../components/TopTalkersTable";
import { api, type Zone, type ZoneNode } from "../api";

const { Title, Paragraph } = Typography;

type ScopeKey = "all" | "cidr" | "zone";

export default function IPTop() {
  const [metric, setMetric] = useState<"bps" | "pps">("bps");
  const [dir, setDir]       = useState<"in" | "out">("in");
  const [geo, setGeo]       = useState<"all" | "ixp">("all");
  const [tab, setTab]       = useState<ScopeKey>("all");

  // ── Range (CIDR) tab ────────────────────────────────────────────────
  // cidrApplied = what the table currently uses (set on Enter / select).
  // prefixOptions = flat list loaded from all zones for autocomplete.
  const [cidrApplied, setCidrApplied]     = useState("");
  const [prefixOptions, setPrefixOptions] = useState<
    { value: string; label: React.ReactNode }[]
  >([]);

  // Load all zone prefixes once for autocomplete suggestions.
  // Pattern: zones() → parallel zoneNodes() per zone → flatten.
  useEffect(() => {
    api.zones()
      .then((zones) =>
        Promise.all(
          zones.map((z: Zone) =>
            api.zoneNodes(z.id)
              .then((nodes: ZoneNode[]) =>
                nodes.map((n) => ({
                  zone: z.name,
                  prefix: n.prefix,
                  desc: n.description,
                }))
              )
              .catch(() => [] as { zone: string; prefix: string; desc: string }[])
          )
        )
      )
      .then((perZone) => {
        const flat = perZone.flat();
        // Deduplicate by prefix (same prefix can appear in multiple zones
        // in theory, though rare). Keep first occurrence.
        const seen = new Set<string>();
        const opts = flat
          .filter(({ prefix }) => {
            if (seen.has(prefix)) return false;
            seen.add(prefix);
            return true;
          })
          .map(({ prefix, desc, zone }) => ({
            value: prefix,
            label: (
              <span style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontFamily: "monospace", fontWeight: 500 }}>{prefix}</span>
                <span style={{ color: "#94a3b8", fontSize: 11, whiteSpace: "nowrap" }}>
                  {desc ? `${desc} · ` : ""}{zone}
                </span>
              </span>
            ),
          }));
        setPrefixOptions(opts);
      })
      .catch(() => setPrefixOptions([]));
  }, []);

  // ── Zone tab ─────────────────────────────────────────────────────────
  const [zones, setZones]           = useState<Zone[]>([]);
  const [selectedZone, setSelectedZone] = useState<number | undefined>(undefined);
  useEffect(() => {
    api.zones()
      .then((z) => {
        setZones(z);
        if (z.length > 0) setSelectedZone(z[0].id);
      })
      .catch(() => setZones([]));
  }, []);

  const geoProp: "ixp" | undefined = geo === "ixp" ? "ixp" : undefined;
  const accentColor = "#fa8c16";
  const tableKey = `${tab}-${cidrApplied}-${selectedZone ?? "_"}-${geo}`;

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>
        <ThunderboltOutlined /> Top IPs by Bandwidth
      </Title>
      <Paragraph type="secondary">
        Live top-talker rankings từ rolling window. Chọn scope tab để lọc —
        mọi tab đều dùng cùng endpoint <code>/api/v1/live/top</code>.
      </Paragraph>

      {/* Common controls */}
      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          options={[
            { label: "Bits/s", value: "bps" },
            { label: "Pkts/s", value: "pps" },
          ]}
          value={metric}
          onChange={(v) => setMetric(v as "bps" | "pps")}
        />
        <Radio.Group
          value={dir}
          onChange={(e) => setDir(e.target.value)}
          optionType="button"
          options={[
            { label: "Inbound", value: "in" },
            { label: "Outbound", value: "out" },
          ]}
        />
        <Tooltip title="IXP only = chỉ traffic international peering">
          <Radio.Group
            value={geo}
            onChange={(e) => setGeo(e.target.value)}
            optionType="button"
            options={[
              { label: "All traffic", value: "all" },
              { label: "IXP only",    value: "ixp" },
            ]}
          />
        </Tooltip>
      </Space>

      <Card size="small" bodyStyle={{ padding: 0 }}>
        <Tabs
          activeKey={tab}
          onChange={(k) => setTab(k as ScopeKey)}
          tabBarStyle={{ paddingLeft: 16, marginBottom: 0 }}
          items={[
            // ── All ──────────────────────────────────────────────────
            {
              key: "all",
              label: "All",
              children: (
                <ScopeBody hint="Top IPs toàn mạng — không filter.">
                  <TableArea
                    key={tableKey}
                    metric={metric} dir={dir} geo={geoProp}
                    filter={undefined} color={accentColor}
                  />
                </ScopeBody>
              ),
            },

            // ── Range (CIDR) ─────────────────────────────────────────
            {
              key: "cidr",
              label: "Range (CIDR)",
              children: (
                <ScopeBody
                  hint="Gõ CIDR hoặc chọn từ danh sách prefix đang có. Enter / click để áp dụng."
                  controls={
                    <AutoComplete
                      style={{ width: 320 }}
                      placeholder="103.213.9.0/24 — gõ để tìm hoặc chọn"
                      options={prefixOptions}
                      filterOption={(inputValue, option) =>
                        (option?.value as string)
                          ?.toLowerCase()
                          .includes(inputValue.toLowerCase())
                      }
                      onSelect={(val: string) => setCidrApplied(val)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const v = (e.target as HTMLInputElement).value.trim();
                          if (v) setCidrApplied(v);
                        }
                      }}
                      allowClear
                      onClear={() => setCidrApplied("")}
                    />
                  }
                >
                  {!cidrApplied ? (
                    <EmptyHint text="Chọn hoặc nhập một CIDR ở trên để xem top IPs." />
                  ) : (
                    <TableArea
                      key={tableKey}
                      metric={metric} dir={dir} geo={geoProp}
                      filter={{ cidr: cidrApplied }} color={accentColor}
                    />
                  )}
                </ScopeBody>
              ),
            },

            // ── Zone ─────────────────────────────────────────────────
            {
              key: "zone",
              label: "Zone",
              children: (
                <ScopeBody
                  hint="Chọn zone — hiển thị top IPs thuộc toàn bộ zone đó."
                  controls={
                    <Select
                      placeholder="Chọn zone…"
                      value={selectedZone}
                      onChange={(v) => setSelectedZone(v)}
                      style={{ width: 240 }}
                      options={zones.map((z) => ({ label: z.name, value: z.id }))}
                      notFoundContent="Chưa có zone nào."
                    />
                  }
                >
                  {!selectedZone ? (
                    <EmptyHint text="Chọn zone ở trên để xem top IPs." />
                  ) : (
                    <TableArea
                      key={tableKey}
                      metric={metric} dir={dir} geo={geoProp}
                      filter={{ zone: selectedZone }} color={accentColor}
                    />
                  )}
                </ScopeBody>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function ScopeBody({
  hint,
  controls,
  children,
}: {
  hint: string;
  controls?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div style={{ padding: 16 }}>
      <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
        {hint}
      </Paragraph>
      {controls && <div style={{ marginBottom: 12 }}>{controls}</div>}
      <div style={{ height: 480 }}>{children}</div>
    </div>
  );
}

function EmptyHint({ text }: { text: string }) {
  return (
    <div style={{
      height: "100%", display: "flex", alignItems: "center",
      justifyContent: "center", color: "#94a3b8", fontSize: 13,
    }}>
      {text}
    </div>
  );
}

function TableArea(props: React.ComponentProps<typeof TopTalkersTable>) {
  return (
    <div style={{ height: "100%" }}>
      <TopTalkersTable {...props} />
    </div>
  );
}
